import { memo, useEffect, useRef, useState } from 'react'

import { Button, CheckIcon, Text } from '@cash-me/react-components'
import NextImage from 'next/image'

import { Polygon } from 'components/polygon'

import { homeRenovationHighlights } from './data'
import * as S from './styles'

type Props = {
  onClick: () => void
}

export const HomeRenovationHowItWorks = memo(({ onClick }: Props) => {
  const sectionRef = useRef<HTMLElement>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const section = sectionRef.current

    if (!section || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          return
        }

        if (entry.boundingClientRect.top > 0) setIsVisible(false)
      },
      { threshold: 0.3 }
    )

    observer.observe(section)

    return () => observer.disconnect()
  }, [])

  const animationClass = isVisible ? 'animate' : ''

  return (
    <S.Section ref={sectionRef} aria-labelledby="home-renovation-how-it-works-title">
      <S.BackgroundImage aria-hidden className={animationClass}>
        <NextImage
          src={`${process.env.NEXT_PUBLIC_SITE_IMAGES_PREFIX}/images/site/home-renovation-loan/how-it-works.webp`}
          alt=""
          fill
          quality={100}
          sizes="(min-width: 1240px) 100vw, 0px"
        />
      </S.BackgroundImage>

      <S.Panel>
        <Text
          id="home-renovation-how-it-works-title"
          as="h2"
          size="heading2"
          weight="medium"
          color="var(--colors-neutral-text-contrast-default)"
        >
          Como funciona o <S.Highlight>Empréstimo para Reforma de Casa?</S.Highlight>
        </Text>

        <S.MobileImage className={animationClass}>
          <NextImage
            src={`${process.env.NEXT_PUBLIC_SITE_IMAGES_PREFIX}/images/site/home-renovation-loan/how-it-works.webp`}
            alt="Família reunida em uma sala reformada"
            fill
            quality={100}
            sizes="calc(100vw - 48px)"
          />
        </S.MobileImage>

        <S.Details>
          <S.Paragraphs>
            <Text size="bodySmall" color="var(--colors-neutral-text-contrast-default)">
              O Empréstimo para Reforma de Casa funciona por meio do <strong>Crédito com Garantia de Imóvel</strong>,
              também conhecido como <strong>home equity</strong>. Você utiliza um imóvel como garantia de pagamento e
              acessa crédito com condições facilitadas.
            </Text>

            <Text size="bodySmall" color="var(--colors-neutral-text-contrast-default)">
              O valor contratado pode ser usado para <strong>reformar, decorar, ampliar ou modernizar</strong> seu
              imóvel. Ou seja, você usa como quiser!
            </Text>

            <Text size="bodySmall" weight="bold" color="var(--colors-neutral-text-contrast-default)">
              Veja como funciona o crédito para reforma de casa:
            </Text>
          </S.Paragraphs>

          <S.HighlightGrid>
            {homeRenovationHighlights.map(({ id, label }) => (
              <S.HighlightCard key={id}>
                <CheckIcon aria-hidden size={24} color="var(--colors-blue-icon-strong)" />
                <Text size="bodySmall" weight="medium" color="var(--colors-neutral-text-contrast-default)">
                  {label}
                </Text>
              </S.HighlightCard>
            ))}
          </S.HighlightGrid>

          <Button
            aria-label="Simular crédito para reforma de casa agora"
            size="lg"
            type="button"
            variants="text-only"
            onClick={onClick}
          >
            Simular agora
          </Button>
        </S.Details>
      </S.Panel>

      <S.Connector aria-hidden>
        <Polygon type="TriangleRound" draw="fill" color="#fa0890" width={67} height={80} />
      </S.Connector>
    </S.Section>
  )
})

HomeRenovationHowItWorks.displayName = 'HomeRenovationHowItWorks'
